import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Box,
  Typography,
  Divider,
  Collapse,
  Tooltip,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  SupportAgent,
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  Assessment as AssessmentIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  ExpandLess,
  ExpandMore,
  Person,
  Store,
  Campaign,
  Facebook,
  TwoWheeler,
  Category,
  AccountBalance,
  Gavel,
  Language,
  CreditCard,
  PointOfSale,
  Speed,
  ShoppingBag,
  Link,
  LocalOffer,
  VpnKey,
  Tune as TuneIcon,
  History,
  GroupWork,
  Description,
  Lock,
  ShoppingCart,
  Inventory2,
  RateReview,
  Api,
  Security,
  Build,
  Event as EventIcon,
  Favorite as DonationIcon,
  Hub as HubIcon,
  VerifiedUser as VerifyIcon,
  Groups as GuiasIcon,
  BeachAccess as BeachIcon,
  ConfirmationNumber as TicketIcon2,
  Star as StarIcon,
  CalendarMonth,
  Public as PublicIcon,
  Category as CategoryIcon,
  Handshake,
  EmojiEvents,
  FolderOpen,
  FormatQuote,
  Help,
  Insights,
  Sms as SmsIcon,
  Email as EmailIcon,
  SwapHoriz as TransferIcon,
  ReceiptLong,
  VerifiedUser as KycIcon,
  Analytics as AnalyticsIcon,
  CreditScore as CreditScoreIcon,
  MonetizationOn,
  Storefront,
  AdminPanelSettings,
  CardGiftcard,
  ViewCarousel,
  LocalShipping,
  ManageSearch,
  ImageSearch,
  Translate as TranslateIcon,
  MoreHoriz,
  Article as ArticleIcon2,
  CloudDownload as DigitalIcon,
  BadgeOutlined,
  GppBad,
  ChatBubbleOutline as ChatIcon,
  QrCode as QrCodeIcon,
  MiscellaneousServices as WAGServicosIcon,
  TvOutlined as DisplayIcon,
  School as SchoolIcon,
  Layers as LayersIcon,
  Nfc as NfcIcon,
  CorporateFare as CorporateFareIcon,
  MonetizationOn as NumisHubIcon,
  AutoAwesome as AutoAwesomeIcon,

} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";

const drawerWidth = 255;
const collapsedWidth = 64;

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path?: string;
  children?: MenuItem[];
  roles?: string[];
  permission?: string; // required permission for PFCOL users
  sandboxOnly?: boolean; // hide in production (feature flag)
}

interface MenuSection {
  label: string;
  items: MenuItem[];
  roles?: string[];
  permissions?: string[]; // show section for PFCOL if user has ANY of these
  sandboxOnly?: boolean;
}

/**
 * O nível acima da secção.
 *
 * Uma secção por serviço arrumou o que estava misturado, mas deixou 22 cabeçalhos
 * no topo — arrumado e à mesma extenso. O grupo junta-os por natureza do trabalho:
 * quem está a tratar do dinheiro do dia não quer ver quinze serviços pelo meio.
 *
 * Um grupo com UMA só secção não desenha o nível extra: mostra a secção
 * directamente. Um clique que não decide nada é um clique a mais.
 */
interface MenuGroup {
  label: string;
  sections: MenuSection[];
}

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  variant?: "permanent" | "temporary";
  collapsed?: boolean;
  onExpand?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ open, onClose, variant = "permanent", collapsed = false, onExpand }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [expandedSection, setExpandedSection] = React.useState<string>('');
  const [expandedGroup, setExpandedGroup] = React.useState<string>('');

  const userRole = user?.role || '';

  const ADM = ['PFADM'];
  const ADM_CON = ['PFADM', 'PFCON'];
  const MERCHANT = ['PFADM', 'PFENT', 'PFLOJ', 'PFDEV'];
  const DEV = ['PFADM', 'PFDEV'];
  const ALL_BUSINESS = ['PFADM', 'PFENT', 'PFDEV', 'PFCON'];

  // GRUPO > SECÇÃO > item. Uma secção por SERVIÇO, e os grupos a juntá-las por
  // natureza do trabalho. Antes disto havia um «Negócio» que era um saco com nove
  // serviços lá dentro, e um «Principal» que misturava operação diária, relatórios
  // e contabilidade; depois vieram 22 secções, arrumadas mas ainda muitas para o
  // topo. Com grupos, o topo tem cinco linhas e cada coisa continua no mesmo sítio.
  const menuGroups: MenuGroup[] = [

  // ════ OPERAÇÃO — o dia-a-dia e o dinheiro ═══════════════════════
  {
    label: t("sidebar.groups.operacao"),
    sections: [

    // ── INÍCIO ────────────────────────────────────────────────────
    {
      label: t("sidebar.sections.principal"),
      items: [
        { text: t("sidebar.dashboard"), icon: <DashboardIcon />, path: "/dashboard" },
        {
          text: t("sidebar.reports"),
          icon: <AssessmentIcon />,
          children: [
            { text: t("sidebar.reports"),            icon: <AssessmentIcon />, path: "/reports", roles: ADM_CON },
            { text: "Business Intelligence",         icon: <Insights />,       path: "/bi",             roles: ADM },
            { text: "Relatório BCN",                 icon: <ArticleIcon2 />,   path: "/reports/bcn",    roles: ADM },
            { text: t("sidebar.paymentDiagnostics"), icon: <AssessmentIcon />, path: "/reports/falhas", roles: ADM },
          ],
        },
      ],
    },

    // ── PAGAMENTOS ────────────────────────────────────────────────
    {
      label: t("sidebar.sections.pagamentos"),
      roles: ADM_CON,
      items: [
        { text: t("sidebar.payments"),     icon: <PaymentIcon />,  path: "/payments" },
        { text: t("sidebar.transacoes"),   icon: <ReceiptLong />,  path: "/transacoes" },
        { text: "Pagamentos com Tokens",   icon: <CreditCard />,   path: "/payments/tokens", roles: ADM },
        // A rota já existia, sem entrada no menu — só se lá chegava escrevendo o
        // URL à mão. É a única página que mostra o código de erro devolvido pela
        // SISP e o histórico de respostas de cada pagamento.
        { text: t("sidebar.sispPayments"), icon: <ManageSearch />, path: "/payments/sisp", roles: ADM },
        // Consulta operacional, não configuração: vem do bloco SISP do Sistema.
        { text: "Estado Transação",        icon: <ManageSearch />, path: "/payments/sisp/transaction-status", roles: ADM },
        { text: "Vendas por POS",          icon: <PointOfSale />,  path: "/payments/sisp/pos-vendas", roles: ADM },
        { text: "Reconciliação SISP",      icon: <ReceiptLong />,  path: "/payments/sisp/reconciliacao", roles: ADM },
        { text: "PagaMe",                  icon: <Link />,         path: "/pagame", roles: ADM },
        { text: "Chat-to-Pay",             icon: <ChatIcon />,     path: "/chat-to-pay", roles: ADM },
      ],
    },

    // ── FINANCEIRO ────────────────────────────────────────────────
    // Junta o que estava espalhado: o Ledger e os Contratos vinham do «Principal»,
    // a Faturação SISP do bloco SISP, e as Taxas de Carregamento estavam em dois
    // sítios ao mesmo tempo — aqui ficam ao lado do Tarifário, que é o seu irmão.
    {
      label: t("sidebar.sections.financial"),
      roles: ADM_CON,
      permissions: ['FAT_VIEW', 'AGT_VIEW', 'AGT_MANAGE'],
      items: [
        { text: "Ledger",                icon: <AccountBalance />,  path: "/ledger",               roles: ADM },
        { text: "Contratos",             icon: <Gavel />,           path: "/contratos",            roles: ADM },
        { text: "Liquidações",           icon: <AccountBalance />,  path: "/liquidacoes",          roles: ADM },
        { text: t("sidebar.billing"),    icon: <ReceiptIcon />,     path: "/billing",              permission: 'FAT_VIEW' },
        { text: "Faturação SISP",        icon: <ReceiptIcon />,     path: "/billing/sisp",         roles: ADM },
        { text: "Tarifário",             icon: <PaymentIcon />,     path: "/pagalishop/tarifario", roles: ADM },
        { text: "Taxas de Carregamento", icon: <MonetizationOn />,  path: "/settings/topup-rates", roles: ADM },
        { text: "Numerário Recebido",    icon: <MonetizationOn />,  path: "/reports/numerario",    roles: ADM },
        { text: "Extracto Comerciantes", icon: <ReceiptIcon />,     path: "/management/extrato-comerciantes", roles: ADM },
        {
          text: t("sidebar.agents"),
          icon: <SupportAgent />,
          permission: 'AGT_VIEW',
          children: [
            { text: t("sidebar.agentList"),   icon: <PeopleIcon />,     path: "/management/agentes",           permission: 'AGT_VIEW' },
            { text: t("sidebar.commissions"), icon: <MonetizationOn />, path: "/management/agentes/comissoes", permission: 'AGT_MANAGE' },
          ],
        },
      ],
    },

    ],
  },

  // ════ NEGÓCIO — os serviços ═════════════════════════════════════
  // O Banco fica aqui e não na Operação: é um serviço como os outros, com o seu
  // próprio conjunto de permissões, e não algo que se veja todos os dias.
  {
    label: t("sidebar.groups.negocio"),
    sections: [

    // ── EVENTOS ───────────────────────────────────────────────────
    {
      label: t("sidebar.sections.eventos"),
      roles: ADM,
      items: [
        { text: t("sidebar.events"), icon: <EventIcon />,   path: "/management/events" },
        { text: "Conferências",      icon: <SchoolIcon />,  path: "/eventscv/conferences" },
        { text: "Transferências",    icon: <TransferIcon />, path: "/eventscv/transfers" },
        { text: "Planos de Eventos", icon: <TicketIcon2 />, path: "/management/plans" },
        { text: "Planos events.cv",  icon: <LayersIcon />,  path: "/management/planos-config" },
        { text: "Chat de Eventos",   icon: <ChatIcon />,    path: "/eventscv/chat" },
      ],
    },

    // ── CASHLESS & NFC ────────────────────────────────────────────
    // Secção própria, e não um submenu de Eventos: as pulseiras e o saldo também
    // servem cantinas e comerciantes, não só o recinto de um evento.
    {
      label: t("sidebar.sections.cashless"),
      roles: ADM,
      // DUAS entradas, porque são dois ecrãs — e mais nenhuma.
      //
      // Antes havia quatro, e três delas abriam a MESMA página noutro separador.
      // O menu ficava a prometer ecrãs que não existiam, e pior: como o separador
      // muda sem o endereço mudar, o realce do menu passava a mentir sobre onde se
      // estava. Um menu que aponta para separadores da mesma página é um índice a
      // duplicar outro índice.
      //
      // «Wallet de Eventos» apontava para /eventscv/events/wallet, rota que nunca
      // existiu — 404 desde sempre.
      //
      // Os separadores Stock NFC, Onboarding NFC e Liquidações BCN vivem dentro da
      // página. Estavam invisíveis porque os endpoints devolviam 500, não por
      // faltar entrada no menu — e isso já está corrigido.
      items: [
        { text: "Cashless & Pulseiras", icon: <NfcIcon />,        path: "/eventscv/wallet" },
        { text: "Estatísticas Wallet",  icon: <AssessmentIcon />, path: "/reports/wallet" },
        { text: "Contratos Comerciante", icon: <Storefront />,    path: "/management/contratos-comerciante" },
        { text: "Liquidação a Comerciantes", icon: <AccountBalance />, path: "/management/liquidacao-comerciantes" },
      ],
    },

    // ── POS PAGALI ────────────────────────────────────────────────
    {
      label: "POS Pagali",
      roles: ADM,
      items: [
        { text: "Terminais",       icon: <PointOfSale />,    path: "/pos/terminais" },
        { text: "Fechos de Caixa", icon: <ReceiptLong />,    path: "/pos/fechos" },
        { text: "Transacções",     icon: <ReceiptIcon />,    path: "/pos/transacoes" },
        { text: "Relatórios",      icon: <AssessmentIcon />, path: "/pos/relatorios" },
        // Configuração do POS na SISP — vem do bloco SISP do Sistema, porque quem
        // trata de terminais é quem precisa dela.
        { text: t("sidebar.pos"),  icon: <PointOfSale />,    path: "/settings/pos-sisp" },
      ],
    },

    // ── PAGALI SHOP ───────────────────────────────────────────────
    {
      label: t("sidebar.pagaliShop"),
      roles: MERCHANT,
      items: [
        { text: t("sidebar.shopDashboard"),   icon: <DashboardIcon />, path: "/pagalishop/dashboard" },
        { text: t("sidebar.products"),        icon: <ShoppingBag />,   path: "/pagalishop" },
        { text: t("sidebar.orders"),          icon: <ShoppingCart />,  path: "/pagalishop/orders" },
        { text: t("sidebar.promotions"),      icon: <LocalOffer />,    path: "/pagalishop/promotions" },
        { text: t("sidebar.inventory"),       icon: <Inventory2 />,    path: "/pagalishop/inventory" },
        { text: t("sidebar.reviews"),         icon: <RateReview />,    path: "/pagalishop/reviews" },
        { text: t("sidebar.digitalProducts"), icon: <DigitalIcon />,   path: "/pagalishop/digital",     roles: ADM },
        { text: t("sidebar.categories"),      icon: <Category />,      path: "/pagalishop/categories" },
        { text: t("sidebar.storeTypes"),      icon: <Store />,         path: "/pagalishop/store-types", roles: ADM },
        { text: t("sidebar.stores"),          icon: <Store />,         path: "/pagalishop/stores",      roles: ADM },
      ],
    },

    // ── PAGALI FOOD ───────────────────────────────────────────────
    {
      label: "Pagali Food",
      roles: ADM,
      items: [
        { text: "Restaurantes", icon: <Store />, path: "/pagalifood/restaurantes" },
        { text: "Taxa de entrega", icon: <LocalShipping />, path: "/pagalifood/entrega" },
        { text: "Banners Promocionais", icon: <ViewCarousel />, path: "/pagalifood/banners" },
      ],
    },

    // ── IWALKCV ───────────────────────────────────────────────────
    {
      label: "iWalkCV",
      roles: ADM,
      items: [
        { text: "Dashboard",        icon: <DashboardIcon />, path: "/iwalkcv" },
        { text: "Operadores",       icon: <VerifyIcon />,    path: "/iwalkcv/operadores" },
        { text: "Guias",            icon: <GuiasIcon />,     path: "/iwalkcv/guias" },
        { text: "Experiências",     icon: <BeachIcon />,     path: "/iwalkcv/experiencias" },
        { text: "Reservas",         icon: <TicketIcon2 />,   path: "/iwalkcv/reservas" },
        { text: "Avaliações",       icon: <StarIcon />,      path: "/iwalkcv/avaliacoes" },
        { text: "Disponibilidades", icon: <CalendarMonth />, path: "/iwalkcv/disponibilidades" },
        { text: "Ilhas",            icon: <PublicIcon />,    path: "/iwalkcv/ilhas" },
        { text: "Categorias",       icon: <CategoryIcon />,  path: "/iwalkcv/categorias" },
        { text: "Hero Slides",      icon: <ViewCarousel />,  path: "/iwalkcv/cms/hero-slides" },
      ],
    },

    // ── CANTINAS ──────────────────────────────────────────────────
    {
      label: t("sidebar.sections.cantinas"),
      roles: ADM,
      items: [
        { text: "Escolas", icon: <SchoolIcon />, path: "/cantinas" },
      ],
    },

    // ── WAG ───────────────────────────────────────────────────────
    {
      label: "WAG",
      roles: ADM,
      items: [
        { text: "Dashboard",        icon: <DashboardIcon />,   path: "/wag" },
        { text: "Agências",         icon: <QrCodeIcon />,      path: "/wag/agencias" },
        { text: "Serviços",         icon: <WAGServicosIcon />, path: "/wag/servicos" },
        { text: "Balcões",          icon: <PointOfSale />,     path: "/wag/balcoes" },
        { text: "Operadores",       icon: <PeopleIcon />,      path: "/wag/operadores" },
        { text: "Display / TV",     icon: <DisplayIcon />,     path: "/wag/display" },
        { text: "Planos & Billing", icon: <ReceiptIcon />,     path: "/wag/billing" },
      ],
    },

    // ── NUMISHUB ──────────────────────────────────────────────────
    {
      label: "NumisHub",
      roles: ADM,
      items: [
        { text: "Tenants / Bancos", icon: <NumisHubIcon />, path: "/numishub" },
      ],
    },

    // ── BANCO ─────────────────────────────────────────────────────
    {
      label: "Banco",
      roles: ADM_CON,
      permissions: ['BNK_VIEW', 'BNK_TRANSFER', 'BNK_CREDIT', 'BNK_KYC'],
      items: [
        { text: t("sidebar.transfers"),   icon: <TransferIcon />,    path: "/banking/transfers",    permission: 'BNK_TRANSFER' },
        { text: t("sidebar.credit"),      icon: <CreditScoreIcon />, path: "/banking/credit",       permission: 'BNK_CREDIT' },
        { text: t("sidebar.kyc"),         icon: <KycIcon />,         path: "/banking/kyc",          permission: 'BNK_KYC' },
        { text: t("sidebar.analytics"),   icon: <AnalyticsIcon />,   path: "/banking/analytics",    permission: 'BNK_VIEW' },
        { text: t("sidebar.openBanking"), icon: <Link />,            path: "/banking/open-banking", permission: 'BNK_VIEW' },
        { text: t("sidebar.scoring"),     icon: <Insights />,        path: "/banking/scoring",      permission: 'BNK_CREDIT' },
        { text: t("sidebar.remessas"),    icon: <Language />,        path: "/banking/remessas",     permission: 'BNK_TRANSFER' },
        { text: 'BCN',                    icon: <AccountBalance />,  path: "/banking/bcn",          permission: 'BNK_TRANSFER' },
      ],
    },

    // ── EMPRESAS B2B ──────────────────────────────────────────────
    {
      label: "Empresas B2B",
      roles: ADM_CON,
      items: [
        { text: "Empresas",      icon: <CorporateFareIcon />, path: "/corporate" },
        { text: "Funcionários",  icon: <PeopleIcon />,        path: "/corporate/employees" },
        { text: "Bracelets NFC", icon: <NfcIcon />,           path: "/corporate/bracelets" },
        { text: "Distribuições", icon: <CardGiftcard />,      path: "/corporate/rewards" },
      ],
    },

    // ── PORTAL EMPRESARIAL (RH da empresa — role PFENT) ───────────
    {
      label: "Portal Empresarial",
      roles: ['PFENT'],
      items: [
        { text: "Dashboard",     icon: <DashboardIcon />, path: "/corporate-portal",               roles: ['PFENT'] },
        { text: "Colaboradores", icon: <PeopleIcon />,    path: "/corporate-portal/employees",     roles: ['PFENT'] },
        { text: "Distribuições", icon: <CardGiftcard />,  path: "/corporate-portal/distributions", roles: ['PFENT'] },
        { text: "Analytics",     icon: <AnalyticsIcon />, path: "/corporate-portal/analytics",     roles: ['PFENT'] },
      ],
    },

    // ── PAGALI JUNIOR ─────────────────────────────────────────────
    {
      label: "Pagali Junior",
      roles: ADM,
      items: [
        { text: "Contas Junior",        icon: <SchoolIcon />,     path: "/junior" },
        { text: "Desafios de Poupança", icon: <MonetizationOn />, path: "/savings" },
      ],
    },

    // ── DONATIVOS ─────────────────────────────────────────────────
    {
      label: t("sidebar.donations"),
      roles: ADM,
      items: [
        { text: t("sidebar.donations"), icon: <DonationIcon />, path: "/management/donations" },
      ],
    },

    // ── HUB & FIDELIZAÇÃO ─────────────────────────────────────────
    {
      label: t("sidebar.sections.hub"),
      roles: ADM,
      items: [
        { text: t("sidebar.hub"),     icon: <HubIcon />,        path: "/hub" },
        { text: "Pagali Voucher",     icon: <CardGiftcard />,   path: "/pagali-voucher" },
        { text: "Cashback",           icon: <MonetizationOn />, path: "/cashback" },
        { text: "Cashback Comercial", icon: <MonetizationOn />, path: "/cashback-comercial" },
      ],
    },

    ],
  },

  // ════ MARKETING & CONTEÚDO ══════════════════════════════════════
  {
    label: t("sidebar.groups.marketingConteudo"),
    sections: [

    // ── MARKETING ─────────────────────────────────────────────────
    {
      label: t("sidebar.marketing"),
      roles: ADM,
      items: [
        { text: t("sidebar.advertising"),               icon: <Campaign />,       path: "/management/advertising" },
        { text: t("sidebar.marketingCampaigns"),        icon: <Campaign />,       path: "/marketing/campaigns" },
        { text: t("sidebar.marketingCashbackRotativo"), icon: <MonetizationOn />, path: "/marketing/cashback-rotativo" },
        { text: t("sidebar.marketingMissoes"),          icon: <EmojiEvents />,    path: "/marketing/missoes" },
        { text: t("sidebar.marketingReferral"),         icon: <Handshake />,      path: "/marketing/referral" },
        { text: t("sidebar.marketingParceiros"),        icon: <Storefront />,     path: "/marketing/parceiros-fundadores" },
        { text: t("sidebar.marketingEmbaixadores"),     icon: <PeopleIcon />,     path: "/marketing/embaixadores" },
        { text: "Leads",                                icon: <EmailIcon />,      path: "/marketing/leads-site" },
        { text: t("sidebar.marketingConteudo"),         icon: <ArticleIcon2 />,   path: "/marketing/conteudo" },
        { text: t("sidebar.marketingMetricas"),         icon: <Insights />,       path: "/marketing/metricas" },
        { text: "Eventos Promocionais",                 icon: <EmojiEvents />,    path: "/marketing/eventos-promo" },
        { text: "Plano de Choque — 200 CVE",            icon: <CardGiftcard />,   path: "/marketing/plano-choque" },
        { text: "Publicidade Mobile",                   icon: <ViewCarousel />,   path: "/marketing/publicidade" },
        // Estas estavam encaminhadas em App.tsx mas nunca chegaram ao menu — só se
        // lá chegava escrevendo o URL à mão.
        { text: "Meta Ads",                             icon: <Facebook />,       path: "/marketing/meta-ads" },
        // O Engagement aparecia duas vezes, aqui e em Comunicação. Fica só aqui.
        { text: t("sidebar.engagement"),                icon: <AutoAwesomeIcon />,path: "/marketing/engagement" },
        { text: "Higgsfield",                           icon: <ViewCarousel />,   path: "/marketing/higgsfield" },
        {
          text: t("sidebar.communication"),
          icon: <EmailIcon />,
          children: [
            { text: t("sidebar.sms"),   icon: <SmsIcon />,   path: "/settings/sms" },
            { text: t("sidebar.email"), icon: <EmailIcon />, path: "/settings/email" },
            { text: "Envios em Massa",  icon: <Campaign />,  path: "/marketing/comunicacao" },
          ],
        },
      ],
    },

    // ── CONTEÚDO ──────────────────────────────────────────────────
    {
      label: t("sidebar.sections.content"),
      roles: ADM,
      items: [
        { text: t("sidebar.pages"),         icon: <FolderOpen />,    path: "/cms/pages" },
        { text: t("sidebar.articles"),      icon: <ArticleIcon2 />,  path: "/cms/blog/posts" },
        { text: t("sidebar.categories"),    icon: <Category />,      path: "/cms/blog/categories" },
        { text: t("sidebar.authors"),       icon: <Person />,        path: "/cms/blog/authors" },
        { text: t("sidebar.testimonials"),  icon: <FormatQuote />,   path: "/cms/testimonials" },
        { text: t("sidebar.partners"),      icon: <Handshake />,     path: "/cms/parceiros" },
        { text: t("sidebar.faq"),           icon: <Help />,          path: "/cms/faq" },
        { text: t("sidebar.loginMessages"), icon: <SmsIcon />,       path: "/cms/login-messages" },
        { text: "Media",                    icon: <ImageSearch />,   path: "/cms/media" },
        { text: "Traduções",                icon: <TranslateIcon />, path: "/cms/translations" },
        { text: t("sidebar.settings"),      icon: <SettingsIcon />,  path: "/cms/settings" },
      ],
    },

    ],
  },

  // ════ ADMINISTRAÇÃO ═════════════════════════════════════════════
  // Grupo de uma só secção: o desenho achata-o e mostra a secção directamente.
  {
    label: t("sidebar.sections.management"),
    sections: [

    // ── ADMINISTRAÇÃO ─────────────────────────────────────────────
    {
      label: t("sidebar.sections.management"),
      roles: ADM,
      items: [
        { text: t("sidebar.users"),         icon: <Person />,            path: "/management/users" },
        { text: t("sidebar.clients"),       icon: <PeopleIcon />,        path: "/management/clients" },
        { text: t("sidebar.entities"),      icon: <BusinessIcon />,      path: "/management/entities" },
        { text: "Leads de Entidades",       icon: <Campaign />,          path: "/management/leads-entidades", roles: ADM },
        { text: t("sidebar.entityGroups"),  icon: <GroupWork />,         path: "/management/entity-groups" },
        { text: "Colaboradores",            icon: <BadgeOutlined />,     path: "/management/colaboradores" },
        { text: "Pagali Drivers",           icon: <TwoWheeler />,        path: "/management/drivers" },
        { text: t("sidebar.permissions"),   icon: <Security />,          path: "/management/permissions" },
        { text: "Alertas de Fraude",        icon: <GppBad />,            path: "/management/fraud-alerts" },
        { text: t("sidebar.notifications"), icon: <NotificationsIcon />, path: "/notifications" },
        {
          text: "Operações",
          icon: <MoreHoriz />,
          children: [
            { text: t("sidebar.emailMigration"),   icon: <EmailIcon />, path: "/management/email-migration" },
            { text: t("sidebar.cniVerifications"), icon: <Lock />,      path: "/management/cni-verifications" },
            { text: "Manutenção",                  icon: <Build />,     path: "/management/maintenance" },
          ],
        },
      ],
    },

    ],
  },

  // ════ SISTEMA ═══════════════════════════════════════════════════
  // Também de uma só secção, pela mesma razão.
  {
    label: t("sidebar.sections.system"),
    sections: [

    // ── SISTEMA ───────────────────────────────────────────────────
    {
      label: t("sidebar.sections.system"),
      items: [
        {
          text: t("sidebar.settings"),
          icon: <SettingsIcon />,
          roles: ADM,
          children: [
            { text: t("sidebar.services"),          icon: <Store />,       path: "/config/services" },
            { text: t("sidebar.serviceCategories"), icon: <Category />,    path: "/settings/service-categories" },
            { text: "Tipos de Serviço",             icon: <Category />,    path: "/settings/service-types" },
            { text: t("sidebar.paymentMethods"),    icon: <PaymentIcon />, path: "/settings/payment-methods" },
            { text: t("sidebar.paymentPages"),      icon: <Description />, path: "/management/payment-pages" },
            { text: t("sidebar.invoices"),          icon: <ReceiptIcon />, path: "/config/invoices" },
            { text: t("sidebar.languages"),         icon: <Language />,    path: "/settings/languages" },
          ],
        },
        {
          text: "App Móvel",
          icon: <Speed />,
          roles: ADM,
          children: [
            { text: "Strings da App", icon: <TranslateIcon />, path: "/settings/app-strings" },
            { text: "Cards da Home",  icon: <ViewCarousel />,  path: "/settings/home-cards" },
          ],
        },
        {
          text: t("sidebar.sisp"),
          icon: <CreditCard />,
          roles: ADM,
          children: [
            { text: t("sidebar.sispServices"), icon: <Link />,           path: "/config/sisp/services" },
            { text: "Tipo de Serviço SISP",    icon: <Link />,           path: "/config/sisp/entities" },
            { text: t("sidebar.tokenization"), icon: <Lock />,           path: "/settings/sisp-tokenization" },
            { text: "Tokens",                  icon: <VpnKey />,         path: "/config/sisp/tokens" },
            { text: "Tipos de Cartão",         icon: <CreditCard />,     path: "/settings/card-types" },
            { text: "Logs",                    icon: <History />,        path: "/settings/sisp-logs" },
            { text: t("sidebar.banks"),        icon: <AccountBalance />, path: "/settings/banks" },
            { text: "CORS Origins",            icon: <Language />,       path: "/settings/cors-origins" },
          ],
        },
        {
          text: t("sidebar.technical"),
          icon: <AdminPanelSettings />,
          roles: [...ADM, 'PFENT', 'PFDEV'],
          children: [
            { text: t("sidebar.api"),      icon: <Api />,                path: "/config/api" },
            { text: t("sidebar.apiKeys"),  icon: <VpnKey />,             path: "/config/api-keys" },
            { text: "Limites de Pagamento",icon: <TuneIcon />,           path: "/config/payment-limits", roles: ADM },
            { text: t("sidebar.oauth"),    icon: <VpnKey />,             path: "/config/oauth",          roles: ADM },
            { text: t("sidebar.logs"),     icon: <History />,            path: "/config/logs",           roles: ADM },
            { text: "Lyra AI",             icon: <SupportAgent />,       path: "/config/lyra",           roles: ADM },
            { text: "Eliminar User",       icon: <AdminPanelSettings />, path: "/config/delete-user",    roles: ADM },
          ],
        },
      ],
    },

    ],
  },

  ];

  const isPFCOL = userRole === 'PFCOL';
  const userPerms = new Set<string>(user?.permissions || []);
  const isSandbox = (process.env.REACT_APP_API_URL || '').includes('sandbox');

  const itemVisible = (item: MenuItem): boolean => {
    if (item.sandboxOnly && !isSandbox) return false;
    if (isPFCOL) {
      if (item.permission) return userPerms.has(item.permission);
      if (item.roles) return false; // role-restricted sem permission equivalente
      return true; // sem restrição — visível a todos
    }
    return !item.roles || item.roles.includes(userRole);
  };

  const filterByRole = (sections: MenuSection[]): MenuSection[] => {
    return sections
      .filter((section) => {
        if (section.sandboxOnly && !isSandbox) return false;
        if (isPFCOL) {
          if (section.permissions) return section.permissions.some((p) => userPerms.has(p));
          if (section.roles) return false;
          return true;
        }
        return !section.roles || section.roles.includes(userRole);
      })
      .map((section) => ({ ...section, items: filterItems(section.items) }))
      .filter((section) => section.items.length > 0);
  };

  const filterItems = (items: MenuItem[]): MenuItem[] => {
    return items
      .filter(itemVisible)
      .map((item) => {
        if (item.children) {
          const filteredChildren = filterItems(item.children);
          return filteredChildren.length > 0 ? { ...item, children: filteredChildren } : null;
        }
        return item;
      })
      .filter(Boolean) as MenuItem[];
  };

  // O grupo não tem papéis nem permissões próprios: herda-os do que sobra dentro
  // dele. Um grupo cujas secções desapareceram todas por falta de permissão
  // desaparece também — senão ficava um cabeçalho que abre para nada.
  const filteredGroups = menuGroups
    .map((group) => ({ ...group, sections: filterByRole(group.sections) }))
    .filter((group) => group.sections.length > 0);

  // Em modo estreito não há cabeçalhos, só ícones: os grupos não se veem.
  const allSections = filteredGroups.flatMap((g) => g.sections);

  // Abre o primeiro grupo e a sua primeira secção, para o menu não abrir fechado.
  React.useEffect(() => {
    if (!expandedGroup && filteredGroups.length > 0) {
      setExpandedGroup(filteredGroups[0].label);
      if (!expandedSection && filteredGroups[0].sections.length > 0) {
        setExpandedSection(filteredGroups[0].sections[0].label);
      }
    }
  }, [filteredGroups.length]);

  const handleNavigation = (path: string) => {
    navigate(path);
    if (variant === "temporary") onClose();
  };

  const toggleExpanded = (text: string) => {
    setExpandedMenus((prev) => (prev.includes(text) ? prev.filter((t) => t !== text) : [...prev, text]));
  };

  const toggleSection = (label: string) => {
    setExpandedSection((prev) => (prev === label ? '' : label));
  };

  // Um grupo de cada vez. Ao trocar de grupo, fecha-se a secção que estava aberta
  // no anterior — senão ao reabrir aparecia meio aberto, com uma secção estendida
  // que já não era a que se estava a ver.
  const toggleGroup = (label: string) => {
    setExpandedGroup((prev) => {
      if (prev === label) return '';
      setExpandedSection('');
      return label;
    });
  };

  const isSelected = (path?: string) => path && location.pathname === path;
  const isParentSelected = (children?: MenuItem[]) =>
    children?.some((child) => child.path && location.pathname.startsWith(child.path));

  const renderMenuItem = (item: MenuItem, level = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus.includes(item.text);
    const selected = isSelected(item.path) || isParentSelected(item.children);

    // Collapsed mode: icon only with tooltip (top-level items only)
    if (collapsed && level === 0) {
      return (
        <React.Fragment key={item.text}>
          <ListItem disablePadding sx={{ display: "block" }}>
            <Tooltip title={item.text} placement="right">
              <ListItemButton
                onClick={() => {
                  if (item.path) handleNavigation(item.path);
                  else if (hasChildren && onExpand) onExpand();
                }}
                selected={!hasChildren && !!selected}
                sx={{
                  minHeight: 44,
                  justifyContent: "center",
                  px: 1,
                  borderRadius: 1,
                  mx: 0.5,
                  mb: 0.3,
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    "&:hover": { backgroundColor: "primary.dark" },
                    "& .MuiListItemIcon-root": { color: "white" },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    justifyContent: "center",
                    color: selected ? "white" : "text.secondary",
                    "& .MuiSvgIcon-root": { fontSize: 20 },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
              </ListItemButton>
            </Tooltip>
          </ListItem>
        </React.Fragment>
      );
    }

    return (
      <React.Fragment key={item.text}>
        <ListItem disablePadding sx={{ display: "block" }}>
          <ListItemButton
            onClick={() => {
              if (hasChildren) toggleExpanded(item.text);
              else if (item.path) handleNavigation(item.path);
            }}
            selected={!hasChildren && !!selected}
            sx={{
              minHeight: 36,
              pl: 1.5 + level * 1.5,
              borderRadius: 1,
              mx: 0.75,
              mb: 0.2,
              "&.Mui-selected": {
                backgroundColor: "primary.main",
                color: "white",
                "&:hover": { backgroundColor: "primary.dark" },
                "& .MuiListItemIcon-root": { color: "white" },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 28,
                color: selected && !hasChildren ? "white" : "text.secondary",
                "& .MuiSvgIcon-root": { fontSize: level > 0 ? 16 : 18 },
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontSize: level > 0 ? 12.5 : 13.5,
                fontWeight: selected ? 600 : 400,
                lineHeight: 1.3,
              }}
            />
            {hasChildren && (isExpanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />)}
          </ListItemButton>
        </ListItem>
        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map((child) => renderMenuItem(child, level + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Toolbar sx={{ px: collapsed ? 0 : 2, minHeight: "56px !important", justifyContent: collapsed ? "center" : "flex-start" }}>
        {collapsed ? (
          <Box
            component="img"
            src="/logo-pagali.png"
            alt="Pagali"
            sx={{ height: 28 }}
            onError={(e: any) => { e.target.style.display = "none"; }}
          />
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              component="img"
              src="/logo-pagali.png"
              alt="Pagali"
              sx={{ height: 28 }}
              onError={(e: any) => { e.target.style.display = "none"; }}
            />
            <Typography variant="subtitle1" fontWeight={700} color="primary">
              Pagali Admin
            </Typography>
          </Box>
        )}
      </Toolbar>
      <Divider />
      <Box sx={{ flex: 1, overflow: "auto", py: 0.5, pt: 1.5 }}>
        {collapsed
          ? // Modo estreito: sem cabeçalhos, só ícones. Os grupos não se veem, mas as
            // secções continuam separadas por um traço.
            allSections.map((section, idx) => (
              <React.Fragment key={section.label}>
                {idx > 0 && <Divider sx={{ my: 0.5 }} />}
                <List disablePadding>
                  {section.items.map((item) => renderMenuItem(item))}
                </List>
              </React.Fragment>
            ))
          : filteredGroups.map((group, gIdx) => {
              // Um grupo com uma só secção não desenha o nível extra: o cabeçalho
              // do grupo abre logo os itens. Um clique que não decide nada sobra.
              const achatado = group.sections.length === 1;
              const seccaoUnica = group.sections[0];
              const aberto = achatado
                ? expandedSection === seccaoUnica.label
                : expandedGroup === group.label;

              const abrir = () => {
                if (achatado) {
                  setExpandedGroup(group.label);
                  toggleSection(seccaoUnica.label);
                } else {
                  toggleGroup(group.label);
                }
              };

              return (
                <React.Fragment key={group.label}>
                  {gIdx > 0 && <Divider sx={{ my: 0.5 }} />}
                  <Box
                    onClick={abrir}
                    sx={{
                      px: 2, pt: 1, pb: 0.4,
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover", borderRadius: 1, mx: 0.5 },
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        color: aberto ? "primary.main" : "text.secondary",
                        fontSize: 11,
                        fontWeight: 800,
                        letterSpacing: 1,
                        lineHeight: 1.8,
                      }}
                    >
                      {group.label}
                    </Typography>
                    {aberto
                      ? <ExpandLess sx={{ fontSize: 15, color: "primary.main" }} />
                      : <ExpandMore sx={{ fontSize: 15, color: "text.secondary" }} />}
                  </Box>

                  <Collapse in={aberto} timeout="auto" unmountOnExit>
                    {achatado ? (
                      <List disablePadding>
                        {seccaoUnica.items.map((item) => renderMenuItem(item))}
                      </List>
                    ) : (
                      group.sections.map((section) => {
                        const seccaoAberta = expandedSection === section.label;
                        return (
                          <React.Fragment key={section.label}>
                            <Box
                              onClick={() => toggleSection(section.label)}
                              sx={{
                                pl: 2.5, pr: 1.5, py: 0.3,
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                cursor: "pointer",
                                "&:hover": { bgcolor: "action.hover", borderRadius: 1, mx: 0.5 },
                              }}
                            >
                              <Typography
                                sx={{
                                  color: seccaoAberta ? "primary.main" : "text.disabled",
                                  fontSize: 11.5,
                                  fontWeight: seccaoAberta ? 700 : 600,
                                  letterSpacing: 0.3,
                                  lineHeight: 1.9,
                                }}
                              >
                                {section.label}
                              </Typography>
                              {seccaoAberta
                                ? <ExpandLess sx={{ fontSize: 13, color: "primary.main" }} />
                                : <ExpandMore sx={{ fontSize: 13, color: "text.disabled" }} />}
                            </Box>
                            <Collapse in={seccaoAberta} timeout="auto" unmountOnExit>
                              <List disablePadding sx={{ pl: 0.5 }}>
                                {section.items.map((item) => renderMenuItem(item))}
                              </List>
                            </Collapse>
                          </React.Fragment>
                        );
                      })
                    )}
                  </Collapse>
                </React.Fragment>
              );
            })}
      </Box>
      <Divider />
      {!collapsed && (
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Pagali Admin v2.0.0
          </Typography>
        </Box>
      )}
    </Box>
  );

  const currentWidth = collapsed ? collapsedWidth : drawerWidth;

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      sx={{
        width: currentWidth,
        flexShrink: 0,
        whiteSpace: "nowrap",
        "& .MuiDrawer-paper": {
          width: currentWidth,
          boxSizing: "border-box",
          borderRight: "1px solid",
          borderColor: "divider",
          overflowX: "hidden",
          transition: (theme) =>
            theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
        },
      }}
    >
      {drawer}
    </Drawer>
  );
};

export default Sidebar;
